import { useState } from 'react'
import { NUDGGE_API_BASE_URL } from '../../lib/config'

const PUSH_FUNCTIONS = [
  {
    name: 'Push-Send',
    description: 'Send push notifications via Firebase Cloud Messaging (FCM v1)',
    method: 'POST',
    endpoint: '/functions/v1/Push-Send',
    features: [
      'Firebase FCM v1 API integration',
      'Tag-based targeting (multiple devices)',
      'OAuth2 JWT signing for FCM authentication',
      'Device token management',
      'Batch notification delivery',
      'Custom click actions and deep linking'
    ],
    businessLogic: [
      { step: 1, title: 'Authentication', description: 'Validates API key from request headers (X-Api-Key or Authorization Bearer token)' },
      { step: 2, title: 'Input Validation', description: 'Ensures title, body, and tag/tagId are provided for notification content and targeting' },
      { step: 3, title: 'Tag Resolution', description: 'Upserts tag string into PushTags table or uses provided tagId for device targeting' },
      { step: 4, title: 'Account Lookup', description: 'Retrieves PushAccount and associated Firebase provider configuration for the API key' },
      { step: 5, title: 'Firebase Config Extraction', description: 'Parses project_id, client_email, private_key from JsonConfig (service account credentials)' },
      { step: 6, title: 'Database Logging', description: 'Creates PushEntries record with title, body, clickUrl, and tagId for audit trail' },
      { step: 7, title: 'Tracking Properties', description: 'Stores custom tracking key-value pairs in PushEntryAttributes for analytics' },
      { step: 8, title: 'Device Token Retrieval', description: 'Queries PushDeviceTags → PushDevices to get all FCM tokens for the target tag' },
      { step: 9, title: 'OAuth2 Token Generation', description: 'Signs JWT with RS256 using private key, exchanges for FCM access token (55min lifetime)' },
      { step: 10, title: 'FCM v1 Batch Send', description: 'Sends individual FCM messages to each device token with notification + data payload, tracks success/failure counts' }
    ]
  },
  {
    name: 'push-device-token-register',
    description: 'Register device tokens for push notification targeting',
    method: 'POST',
    endpoint: '/functions/v1/push-device-token-register',
    features: [
      'FCM device token registration',
      'Tag-based device grouping',
      'Automatic token refresh handling',
      'Device metadata storage',
      'Multi-device per user support',
      'Platform detection (web, iOS, Android)'
    ],
    businessLogic: [
      { step: 1, title: 'Authentication', description: 'Validates API key from request headers' },
      { step: 2, title: 'Input Validation', description: 'Ensures deviceToken and tag are provided' },
      { step: 3, title: 'Account Lookup', description: 'Retrieves PushAccount for the API key' },
      { step: 4, title: 'Tag Resolution', description: 'Creates or retrieves PushTag record for the given tag string' },
      { step: 5, title: 'Device Upsert', description: 'Creates or updates PushDevice record with the FCM token' },
      { step: 6, title: 'Tag Association', description: 'Links device to tag via PushDeviceTags junction table' },
      { step: 7, title: 'Metadata Storage', description: 'Stores device info (platform, browser, OS) for analytics' },
      { step: 8, title: 'Return Confirmation', description: 'Returns device and tag IDs for client-side tracking' }
    ]
  },
  {
    name: 'Send-Push-Templated',
    description: 'Send push notifications using stored templates with variable interpolation',
    method: 'POST',
    endpoint: '/functions/v1/Send-Push-Templated',
    features: [
      'Template-based push notifications',
      'Variable interpolation ({{ }})',
      'Draft/production template support',
      'Firebase Legacy API (FCM HTTP v1)',
      'Single device targeting',
      'Custom data payload'
    ],
    businessLogic: [
      { step: 1, title: 'Authentication', description: 'Validates API key from request headers' },
      { step: 2, title: 'Input Validation', description: 'Ensures toToken (FCM device token), templateIdentifier (UUID), and templateParameters (object) are provided' },
      { step: 3, title: 'Account Lookup', description: 'Retrieves PushAccount and associated provider configuration for the API key' },
      { step: 4, title: 'Template Retrieval', description: 'Fetches PushTemplate by templateIdentifier for the account (checks Title/Body or DraftTitle/DraftBody)' },
      { step: 5, title: 'Variable Interpolation', description: 'Replaces {{ variableName }} placeholders in title and body with templateParameters values' },
      { step: 6, title: 'Firebase Config', description: 'Extracts ServerKey, DefaultIcon, and DefaultClickAction from PushProviderConfig.JsonConfig' },
      { step: 7, title: 'Database Logging', description: 'Creates PushEntries record with TemplateIdentifier, interpolated title/body, and device token' },
      { step: 8, title: 'Tracking Properties', description: 'Stores custom tracking attributes in PushEntryAttributes for analytics' },
      { step: 9, title: 'FCM Payload Build', description: 'Constructs FCM legacy API payload with notification (title, body, icon, click_action) and data (entryId, tag, clickUrl)' },
      { step: 10, title: 'Send & Response', description: 'POSTs to fcm.googleapis.com/fcm/send, updates PushEntries.ExternalId with message_id, returns 202 with pushEntryId' }
    ]
  },
  {
    name: 'Push-Webhook',
    description: 'Handle push notification delivery and interaction webhooks',
    method: 'POST',
    endpoint: '/functions/v1/Push-Webhook',
    features: [
      'Delivery event tracking (delivered, clicked, closed)',
      'Automatic event type creation',
      'Secret-based authentication',
      'Batch event processing',
      'Device token tracking',
      'Metadata storage'
    ],
    businessLogic: [
      { step: 1, title: 'Secret Verification', description: 'Validates X-Webhook-Secret header against PUSH_WEBHOOK_SECRET environment variable (optional)' },
      { step: 2, title: 'Parse Events', description: 'Parses JSON body into array of events (supports batch webhook calls)' },
      { step: 3, title: 'Event Type Management', description: 'For each event, gets or creates PushEventType record (e.g., "delivered", "clicked", "closed")' },
      { step: 4, title: 'Extract Event Data', description: 'Parses event fields: entryId (pushEntryId), status, deviceToken, clickUrl, timestamp, meta' },
      { step: 5, title: 'Validate PushEntry', description: 'Checks if PushEntry exists for the given entryId (skips if not found instead of creating)' },
      { step: 6, title: 'Timestamp Normalization', description: 'Converts timestamp to ISO 8601 format (handles numeric milliseconds or string formats)' },
      { step: 7, title: 'Store Delivery Event', description: 'Inserts into PushEntryDeliveryEvents with event type, status, timestamp, device token, and metadata' },
      { step: 8, title: 'Error Handling', description: 'Logs errors per event but continues processing remaining events in batch' },
      { step: 9, title: 'Event Cache', description: 'Caches event type IDs in memory to reduce database lookups for repeated statuses' },
      { step: 10, title: 'Return Summary', description: 'Returns 202 with count of events received and per-event success/failure results' }
    ]
  }
]

// Sample log entries to show format
const SAMPLE_LOGS = [
  { method: 'POST', status: 202, function: 'Push-Send', time: 1284, message: 'Notification sent successfully' },
  { method: 'POST', status: 202, function: 'Push-Webhook', time: 715, message: 'Delivery event recorded' },
  { method: 'POST', status: 200, function: 'push-device-token-register', time: 580, message: 'Device registered' },
  { method: 'POST', status: 400, function: 'Push-Send', time: 763, message: 'Missing required field: tag' },
  { method: 'POST', status: 401, function: 'Push-Send', time: 274, message: 'Invalid API key' },
  { method: 'POST', status: 500, function: 'Push-Send', time: 446, message: 'FCM authentication failed' },
]

export default function EdgeFunctionsTab() {
  const [selectedFunction, setSelectedFunction] = useState(null)
  const [logsOpen, setLogsOpen] = useState(false)

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30'
    if (status >= 400 && status < 500) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30'
    return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30'
  }

  const supabaseLogsUrl = 'https://supabase.com/dashboard/project/ymxdrorugrieltcxycof/logs/edge-functions'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                Edge Functions
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Push notification Supabase Edge Functions documentation and logs
              </p>
            </div>
          </div>
          <a
            href={supabaseLogsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex items-center space-x-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            <span>Open Supabase Logs</span>
          </a>
        </div>
      </div>

      {/* Live Logs Accordion */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <button
          onClick={() => setLogsOpen(!logsOpen)}
          className="w-full px-4 sm:px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition"
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Live Logs</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">View edge function execution logs (last 24 hours)</p>
            </div>
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${logsOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {logsOpen && (
          <div className="border-t border-gray-200 dark:border-gray-700">
            {/* Info banner */}
            <div className="px-4 sm:px-6 py-4 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    For detailed logs with full request/response data, use the Supabase Dashboard.
                    The preview below shows the log format you'll see.
                  </p>
                  <a
                    href={supabaseLogsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 mt-2"
                  >
                    <span>Open Supabase Logs Explorer</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Sample logs preview */}
            <div className="px-4 sm:px-6 py-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Log Format Preview</h4>
              <div className="space-y-2 font-mono text-xs">
                {SAMPLE_LOGS.map((log, idx) => (
                  <div
                    key={idx}
                    className="flex items-center space-x-3 p-2 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700"
                  >
                    <span className={`px-2 py-0.5 rounded font-semibold ${getStatusColor(log.status)}`}>
                      {log.status}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 w-12">{log.method}</span>
                    <span className="text-purple-600 dark:text-purple-400 font-medium flex-shrink-0">
                      {log.function}
                    </span>
                    <span className="text-gray-600 dark:text-gray-300 flex-1 truncate">
                      {log.message}
                    </span>
                    <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">
                      {log.time}ms
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* What to look for */}
            <div className="px-4 sm:px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Status Code Reference</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                    200/202
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">Success</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400">
                    400/401
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">Client Error</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                    500
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">Server Error</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Function List */}
        <div className="lg:col-span-1 space-y-3">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 px-1">Available Functions</h3>
          {PUSH_FUNCTIONS.map((func) => (
            <button
              key={func.name}
              onClick={() => setSelectedFunction(func)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                selectedFunction?.name === func.name
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-700'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                      {func.name}
                    </h3>
                    <span className="px-2 py-0.5 text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                      {func.method}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {func.description}
                  </p>
                  <code className="text-xs text-purple-600 dark:text-purple-400 mt-2 block">
                    {func.endpoint}
                  </code>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Function Details */}
        <div className="lg:col-span-2">
          {selectedFunction ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
              {/* Function Header */}
              <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-4 sm:p-6 text-white">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold">{selectedFunction.name}</h3>
                    <p className="text-purple-100 mt-1 text-sm sm:text-base">{selectedFunction.description}</p>
                  </div>
                  <button
                    onClick={() => setSelectedFunction(null)}
                    className="p-1 hover:bg-white/20 rounded-lg transition"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                    {selectedFunction.method}
                  </span>
                  <code className="px-3 py-1 bg-black/20 rounded-full text-sm font-mono">
                    {selectedFunction.endpoint}
                  </code>
                </div>
              </div>

              {/* Features */}
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Key Features
                </h4>
                <ul className="space-y-2">
                  {selectedFunction.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-2 text-sm">
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Business Logic Flow */}
              <div className="p-4 sm:p-6">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                  Business Logic Flow
                </h4>
                <div className="space-y-3">
                  {selectedFunction.businessLogic.map((logic, idx) => (
                    <div
                      key={idx}
                      className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {logic.step}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                          {logic.title}
                        </h5>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          {logic.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* API Example */}
              <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  API Example
                </h4>
                <pre className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 overflow-x-auto text-xs">
                  <code className="text-gray-800 dark:text-gray-200">{`curl -X ${selectedFunction.method} \\
  '${NUDGGE_API_BASE_URL}${selectedFunction.endpoint}' \\
  -H 'X-Api-Key: your-api-key' \\
  -H 'Content-Type: application/json' \\
  -d '{
  // Request payload here
}'`}</code>
                </pre>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 sm:p-12 text-center">
              <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Select a Function
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Choose an Edge Function from the list to view its implementation and documentation
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
